import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePermission } from '@/components/guards/permission-guard';
import { ArrowLeft, Handshake, Loader2, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DealerModal } from '@/components/trading/cari/dealer-modal';
import { SupplierDetailContent } from '@/components/trading/cari/supplier-detail-content';
import { trpc } from '@/lib/trpc';

export function DealerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const canEdit = usePermission('CUSTOMERS', 'EDIT', 'TRADING');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data: supplier, isLoading, error } = trpc.tradingSupplier.byId.useQuery(
    id!,
    { enabled: !!id }
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2 text-muted-foreground">{tCommon('loading')}</span>
      </div>
    );
  }

  if (error || !supplier) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate('/trading/cari?type=B')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('cari.title')}
        </Button>
        <div className="border border-red-300 bg-red-50 p-4 text-red-700">
          <p className="font-medium">{tCommon('error')}</p>
          <p className="text-sm">{error?.message || tCommon('notFound')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/trading/cari?type=B')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('cari.title')}
          </Button>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center bg-green-100">
              <Handshake className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">{supplier.companyName}</h1>
              {supplier.tradeName && (
                <p className="text-muted-foreground">{supplier.tradeName}</p>
              )}
            </div>
          </div>
        </div>
        {canEdit && (
          <Button onClick={() => setIsModalOpen(true)}>
            <Pencil className="mr-2 h-4 w-4" />
            {tCommon('edit')}
          </Button>
        )}
      </div>

      <DealerModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        dealer={{
          id: supplier.id,
          companyName: supplier.companyName,
          contactName: supplier.contactName,
          email: supplier.email,
          phone: supplier.phone,
          website: supplier.website,
          address: supplier.addressLine1,
          city: supplier.city,
          country: supplier.country,
          taxId: supplier.taxId,
          paymentTerms: supplier.paymentTerms as any,
          notes: supplier.notes,
          isActive: supplier.isActive,
        }}
      />

      <SupplierDetailContent cariType="B" supplier={supplier} />
    </div>
  );
}
